import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Store } from "lucide-react";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { DEFAULT_PAGE_CONTENT } from "@/lib/websiteContentDefaults";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * useWebsiteContent — fetches published content for a page_key with the
 * hardcoded frontend defaults as a fail-safe fallback. Public routes must
 * never blank out on a temporary backend/DB issue.
 */
export function useWebsiteContent(pageKey) {
  const [state, setState] = useState({
    loading: true,
    title: DEFAULT_PAGE_CONTENT[pageKey]?.title || "",
    content: DEFAULT_PAGE_CONTENT[pageKey]?.content || {},
    from: "fallback",
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/website-content/${pageKey}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setState({
          loading: false,
          title: data.title || DEFAULT_PAGE_CONTENT[pageKey]?.title || "",
          content: data.content || DEFAULT_PAGE_CONTENT[pageKey]?.content || {},
          from: "api",
        });
      } catch {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, from: "fallback" }));
      }
    })();
    return () => { cancelled = true; };
  }, [pageKey]);
  return state;
}

/**
 * SafeHtml — renders admin-editable HTML using DOMPurify to strip <script>,
 * event handlers, and other unsafe constructs.
 */
export function SafeHtml({ html, testid }) {
  if (!html) return null;
  // Lazy load DOMPurify (already installed via lucide-react's peer)? Not present —
  // ship an inline minimal sanitiser sufficient for our use: strip <script>,
  // strip on*= handlers, strip javascript: hrefs. This matches the same set of
  // constructs blocked by DOMPurify's default whitelist for our content shape.
  const clean = String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/?(iframe|object|embed|link|meta|form|input|button)[^>]*>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/(href|src)=(["'])\s*javascript:[^"']*\2/gi, '$1="#"');
  return <div className="public-html" data-testid={testid} dangerouslySetInnerHTML={{ __html: clean }} />;
}

/* ============================================================ shell ==== */
function PublicShell({ eyebrow, title, testid, children }) {
  return (
    <div className="public-page" data-testid={testid}>
      <header className="public-header">
        <Link to="/" className="public-brand" data-testid="public-brand-link">
          <strong>Barter India</strong>
          <span className="muted small">Trade More. Pay Less. Grow Together.</span>
        </Link>
        <nav className="public-nav">
          <Link to="/pricing" data-testid="nav-pricing">Pricing</Link>
          <Link to="/contact" data-testid="nav-contact">Contact</Link>
          <Link to="/login" className="secondary-button" data-testid="nav-login">Log in</Link>
        </nav>
      </header>
      <main className="public-main">
        <div className="public-body">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          {title && <h1 data-testid="public-page-title">{title}</h1>}
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* -------------------------------------- generic legal / info page ------ */
const LEGAL_SEO = {
  terms:     { path: "/terms",     title: "Terms of Service · Barter India",           description: "The Terms of Service that govern use of Barter India, the B2B barter marketplace connecting verified Indian businesses." },
  privacy:   { path: "/privacy",   title: "Privacy Policy · Barter India",              description: "How Barter India collects, uses, and protects business and personal information across the barter marketplace." },
  refund:    { path: "/refund",    title: "Refund & Cancellation Policy · Barter India", description: "Refund and cancellation rules for Barter India subscription fees, facilitation fees, and trade credit allocations." },
  shipping:  { path: "/shipping",  title: "Shipping & Delivery Policy · Barter India",   description: "How shipping, delivery, and physical fulfilment between traders are handled within Barter India barter transactions." },
  contact:   { path: "/contact",   title: "Contact Barter India",                        description: "Reach the Barter India support team for onboarding, verification, subscriptions, billing, or trade queries." },
  grievance: { path: "/grievance", title: "Grievance Redressal · Barter India",          description: "Barter India grievance redressal process and grievance officer contact details for member complaints." },
};

function LegalPage({ pageKey, eyebrow, testid }) {
  const { title, content } = useWebsiteContent(pageKey);
  const seo = LEGAL_SEO[pageKey];
  return (
    <PublicShell testid={testid} eyebrow={eyebrow} title={title}>
      {seo && <Seo title={seo.title} description={seo.description} path={seo.path} />}
      <SafeHtml html={content.body_html} testid={`${testid}-body`} />
    </PublicShell>
  );
}

export const TermsPage     = () => <LegalPage pageKey="terms"     eyebrow="LEGAL"   testid="page-terms" />;
export const PrivacyPage   = () => <LegalPage pageKey="privacy"   eyebrow="LEGAL"   testid="page-privacy" />;
export const RefundPage    = () => <LegalPage pageKey="refund"    eyebrow="POLICY"  testid="page-refund" />;
export const ShippingPage  = () => <LegalPage pageKey="shipping"  eyebrow="POLICY"  testid="page-shipping" />;
export const ContactPage   = () => <LegalPage pageKey="contact"   eyebrow="SUPPORT" testid="page-contact" />;
export const GrievancePage = () => <LegalPage pageKey="grievance" eyebrow="SUPPORT" testid="page-grievance" />;

/* ================================================================ PRICING */
export function PricingPage() {
  const { title, content } = useWebsiteContent("pricing");
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/subscription-plans`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setPlans(await r.json());
      } catch {
        setPlansError("Could not load current plans. Please email admin@barterindia.in for the latest.");
      } finally { setPlansLoading(false); }
    })();
  }, []);

  return (
    <PublicShell testid="page-pricing" eyebrow="PRICING" title={title}>
      <Seo
        title="Pricing & Subscription Plans · Barter India"
        description="Current Barter India subscription plans, one-time fees, credit multipliers, and initial trade-credit limits for verified Indian businesses."
        path="/pricing"
      />
      {content.intro && <p data-testid="pricing-intro">{content.intro}</p>}

      {plansLoading && <p className="muted">Loading current plans…</p>}
      {plansError && <div className="form-error" data-testid="pricing-error">{plansError}</div>}

      {!plansLoading && plans.length > 0 && (
        <div className="pricing-grid" data-testid="pricing-grid">
          {plans.map((p) => (
            <article key={p.id} className={`pricing-card ${p.is_recommended ? "recommended" : ""}`} data-testid={`pricing-card-${p.name.toLowerCase()}`}>
              {p.is_recommended && <span className="chip recommended-chip" data-testid={`pricing-recommended-${p.name.toLowerCase()}`}>Recommended</span>}
              <h3>{p.name}</h3>
              <div className="pricing-fee">
                <span className="amount">₹{p.subscription_fee.toLocaleString("en-IN")}</span>
                <span className="muted small">one-time subscription</span>
              </div>
              <ul className="pricing-list">
                <li><strong>Credit multiplier:</strong> {p.credit_multiplier}×</li>
                <li><strong>Initial credit limit:</strong> ₹{p.initial_credit_limit.toLocaleString("en-IN")}</li>
                <li><strong>Validity:</strong> {p.validity_days} days</li>
                {p.description && <li>{p.description}</li>}
              </ul>
            </article>
          ))}
        </div>
      )}

      <SafeHtml html={content.footer_notes_html} testid="pricing-footer-notes" />
    </PublicShell>
  );
}

/* ================================================================== HOME */
export function HomePage() {
  const { content } = useWebsiteContent("home");
  const c = { ...(DEFAULT_PAGE_CONTENT.home?.content || {}), ...(content || {}) };
  const trustStrip = Array.isArray(c.trust_strip_items) ? c.trust_strip_items : [];
  const steps = Array.isArray(c.how_it_works_steps) ? c.how_it_works_steps : [];
  const benefits = Array.isArray(c.benefits_items) ? c.benefits_items : [];
  const trustPoints = Array.isArray(c.trust_section_points) ? c.trust_section_points : [];
  const [preview, setPreview] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/public/marketplace/preview?limit=6`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setPreview(Array.isArray(d.items) ? d.items : []))
      .catch(() => setPreview([]));
  }, []);

  const finalPrimary = c.final_cta_primary_text || c.final_cta_button;
  const finalPrimaryHref = c.final_cta_primary_href || c.final_cta_href || "/login";

  return (
    <div className="public-page home-page-v2" data-testid="page-home">
      <Seo
        title="Barter India — B2B Barter Marketplace for Verified Indian Businesses"
        description="Barter India is a B2B barter marketplace helping verified Indian businesses trade goods and services using Trade Credits, without spending cash upfront."
        path="/"
      />
      <header className="public-header home-header-v2">
        <Link to="/" className="public-brand" data-testid="public-brand-link">
          <strong>Barter India</strong>
          <span className="muted small">Trade More. Pay Less. Grow Together.</span>
        </Link>
        <nav className="public-nav">
          <Link to="/pricing" data-testid="nav-pricing">Pricing</Link>
          <Link to="/contact" data-testid="nav-contact">Contact</Link>
          <Link to="/login" className="secondary-button" data-testid="nav-login">Log in</Link>
        </nav>
      </header>

      <main className="public-main home-main-v2">
        {/* ============= 1. HERO ============= */}
        <section className="home-hero-v2" data-testid="home-hero">
          <div className="home-hero-copy">
            <p className="home-eyebrow" data-testid="home-eyebrow">{c.hero_eyebrow || "B2B BARTER MARKETPLACE"}</p>
            <h1 data-testid="home-hero-headline">{c.hero_headline}</h1>
            <p className="home-hero-supporting" data-testid="home-hero-supporting">{c.hero_supporting}</p>
            <div className="home-cta-row">
              {c.primary_cta_text && (
                <Link to={c.primary_cta_href || "/login"} className="primary-button" data-testid="home-primary-cta">
                  {c.primary_cta_text} <ArrowRight size={14} />
                </Link>
              )}
              {c.secondary_cta_text && (
                <Link to={c.secondary_cta_href || "/login"} className="ghost-button" data-testid="home-secondary-cta">
                  {c.secondary_cta_text}
                </Link>
              )}
            </div>
          </div>
          <div className="home-hero-visual" aria-hidden="true">
            <div className="home-flow-diagram">
              <div className="home-flow-node home-flow-node-a">
                <span className="home-flow-node-eyebrow">BUSINESS A</span>
                <span className="home-flow-node-label">Products / Services</span>
              </div>
              <div className="home-flow-arrow home-flow-arrow-down" />
              <div className="home-flow-node home-flow-node-mid">
                <span className="home-flow-node-eyebrow">BARTER INDIA</span>
                <span className="home-flow-node-label">Barter Credits</span>
              </div>
              <div className="home-flow-arrow home-flow-arrow-down" />
              <div className="home-flow-node home-flow-node-b">
                <span className="home-flow-node-eyebrow">BUSINESS B</span>
                <span className="home-flow-node-label">Value Exchanged</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============= 2. TRUST STRIP ============= */}
        {c.trust_strip_visible !== false && trustStrip.length > 0 && (
          <section className="home-trust-strip" data-testid="home-trust-strip">
            {trustStrip.map((it, i) => (
              <div key={i} className="home-trust-strip-item" data-testid={`home-trust-strip-item-${i}`}>
                <ShieldCheck size={16} className="home-trust-strip-icon" aria-hidden="true" />
                <div>
                  <strong>{it.title}</strong>
                  {it.description && <p className="muted small">{it.description}</p>}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* ============= 3. HOW IT WORKS ============= */}
        {c.how_it_works_visible !== false && steps.length > 0 && (
          <section className="home-section-v2" data-testid="home-how-it-works">
            <header className="home-section-heading">
              <h2>{c.how_it_works_heading}</h2>
              {c.how_it_works_supporting && <p className="home-section-supporting">{c.how_it_works_supporting}</p>}
            </header>
            <ol className="home-journey">
              {steps.map((s, i) => (
                <li key={i} className="home-journey-step" data-testid={`home-step-${i}`}>
                  <span className="home-journey-number">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* ============= 4. BENEFITS ============= */}
        {c.benefits_visible !== false && benefits.length > 0 && (
          <section className="home-section-v2 home-benefits" data-testid="home-benefits">
            <header className="home-section-heading">
              <h2>{c.benefits_heading}</h2>
              {c.benefits_supporting && <p className="home-section-supporting">{c.benefits_supporting}</p>}
            </header>
            <div className="home-benefits-grid">
              {benefits.map((b, i) => (
                <article key={i} className="home-benefit-item" data-testid={`home-benefit-${i}`}>
                  <h3>{b.title}</h3>
                  <p>{b.description}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ============= 5. DIRECT + CIRCULAR ============= */}
        {c.barter_types_visible !== false && (
          <section className="home-section-v2 home-barter-types" data-testid="home-barter-types">
            <header className="home-section-heading">
              <h2>{c.barter_types_heading || "More Than Traditional Barter"}</h2>
              {c.barter_types_supporting && <p className="home-section-supporting">{c.barter_types_supporting}</p>}
            </header>
            <div className="home-barter-types-grid">
              <article className="home-barter-panel" data-testid="home-barter-direct">
                <p className="home-barter-eyebrow">DIRECT</p>
                <h3>{c.direct_barter_title || "Direct Barter"}</h3>
                <div className="home-barter-diagram">
                  <span className="home-node">A</span>
                  <span className="home-node-arrow">↕</span>
                  <span className="home-node">B</span>
                </div>
                <p>{c.direct_barter_description}</p>
              </article>
              <article className="home-barter-panel" data-testid="home-barter-circular">
                <p className="home-barter-eyebrow">CIRCULAR</p>
                <h3>{c.circular_barter_title || "Circular Barter"}</h3>
                <div className="home-barter-diagram home-barter-diagram-triangle">
                  <span className="home-node home-node-top">A</span>
                  <span className="home-node home-node-left">C</span>
                  <span className="home-node home-node-right">B</span>
                  <svg className="home-triangle-lines" viewBox="0 0 200 170" aria-hidden="true">
                    <path d="M100 30 L172 140 L28 140 Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" />
                    <polygon points="100,25 96,35 104,35" fill="currentColor" />
                    <polygon points="172,140 162,138 165,146" fill="currentColor" />
                    <polygon points="28,140 34,142 32,134" fill="currentColor" />
                  </svg>
                </div>
                <p>{c.circular_barter_description}</p>
              </article>
            </div>
          </section>
        )}

        {/* ============= 6. MARKETPLACE PREVIEW ============= */}
        {c.marketplace_preview_visible !== false && (
          <section className="home-section-v2 home-market-preview" data-testid="home-market-preview">
            <header className="home-section-heading home-section-heading-inline">
              <div>
                <h2>{c.marketplace_preview_heading}</h2>
                {c.marketplace_preview_supporting && <p className="home-section-supporting">{c.marketplace_preview_supporting}</p>}
              </div>
              <Link to="/login" className="ghost-button" data-testid="home-market-preview-cta-top">
                {c.marketplace_preview_cta || "Explore Marketplace"} <ArrowRight size={13} />
              </Link>
            </header>
            {preview.length === 0 ? (
              <div className="home-market-empty" data-testid="home-market-empty">
                <Store size={20} />
                <p><strong>Marketplace is warming up.</strong></p>
                <p className="muted small">Verified businesses will start listing here soon.</p>
              </div>
            ) : (
              <div className="home-market-grid" data-testid="home-market-grid">
                {preview.map((l) => (
                  <article key={l.id} className="home-market-card" data-testid={`home-market-card-${l.id}`}>
                    <div className="home-market-card-top">
                      <span className="chip chip-info">{l.listing_type}</span>
                      {l.verified && <span className="chip status-verified home-market-verified-chip"><ShieldCheck size={11} /> Verified</span>}
                    </div>
                    <h3>{l.title}</h3>
                    {l.company_brand && <p className="muted small">By {l.company_brand}</p>}
                    <dl className="home-market-meta">
                      {l.category_name && (<><dt>Category</dt><dd>{l.category_name}</dd></>)}
                      {l.city_name && (<><dt>Location</dt><dd>{l.city_name}</dd></>)}
                      {l.indicative_value != null && (<><dt>Indicative</dt><dd>₹{Number(l.indicative_value).toLocaleString("en-IN")}</dd></>)}
                    </dl>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ============= 7. TRUST SECTION ============= */}
        {c.trust_section_visible !== false && trustPoints.length > 0 && (
          <section className="home-section-v2 home-trust-section" data-testid="home-trust-section">
            <header className="home-section-heading">
              <h2>{c.trust_section_heading}</h2>
              {c.trust_section_supporting && <p className="home-section-supporting">{c.trust_section_supporting}</p>}
            </header>
            <div className="home-trust-grid">
              {trustPoints.map((t, i) => (
                <article key={i} className="home-trust-point" data-testid={`home-trust-point-${i}`}>
                  <span className="home-trust-dot" aria-hidden="true" />
                  <h3>{t.title}</h3>
                  <p>{t.description}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ============= 8. FINAL CTA ============= */}
        <section className="home-final-cta-v2" data-testid="home-final-cta">
          <div className="home-final-cta-inner">
            <h2>{c.final_cta_heading || "Turn What Your Business Has Into What Your Business Needs."}</h2>
            <p>{c.final_cta_description}</p>
            <div className="home-cta-row">
              {finalPrimary && (
                <Link to={finalPrimaryHref} className="primary-button" data-testid="home-final-cta-primary">
                  {finalPrimary} <ArrowRight size={14} />
                </Link>
              )}
              {(c.final_cta_secondary_text) && (
                <Link to={c.final_cta_secondary_href || "/login"} className="ghost-button ghost-button-inverse" data-testid="home-final-cta-secondary">
                  {c.final_cta_secondary_text}
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
